import React, { useMemo, useState, useEffect } from 'react';
import {
	Box,
	Typography,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocation, useParams } from 'react-router-dom';
import { TaskAccordion } from '@/components/TaskAccordion';
import { getTime, isAfter } from 'date-fns';
import { Configure, Index, useHits } from 'react-instantsearch';
import { TableRefresh } from '../table/TableRefresh';
import { safelyParseDate } from '@/utils/date';

const today = new Date();
const now = () => getTime(today);

const getTaskFilter = (tab) => {
	switch (tab) {
		case 'overdue':
			return `completed:=${false} && dueDate:<${now()}`;
		case 'completed':
			return `completed:=${true}`;
		default:
			return `completed:=${false} && dueDate:>=${now()}`;
	}
};

const TaskTabContent = ({ tab, expanded, setExpanded }) => {
	const params = useParams();
	const isDeal = Boolean(params?.dealId);
	const filterSchema = useMemo(() => {
		const objectFilter = params?.relationshipCompanyId
			? `relationshipCompanyId := ${params.relationshipCompanyId}`
			: isDeal
				? `dealId := ${params?.dealId}`
				: `clientId := ${params?.clientId}`;

		const tabFilter = getTaskFilter(tab);

		return {
			filters: `${tabFilter} && ${objectFilter}`,
		};
	}, [tab, isDeal, params?.dealId, params?.clientId, params?.relationshipCompanyId]);

	return (
		<Index indexName='tasks'>
			<Configure {...filterSchema} />
			<TableRefresh table={'taskTable'} />
			<Accordion
				data-testid={`${tab}-tasks`}
				expanded={expanded}
				onChange={setExpanded(tab)}
				disableGutters
				sx={{
					backgroundColor: 'transparent',
					boxShadow: 'none',
					'&.MuiAccordion-root:before': {
						display: 'none',
					},
					'&:not(:last-child)': {
						borderBottom: 0,
					},
					'&:before': {
						display: 'none',
					},
				}}
			>
				<HitsAccordionContents tab={tab} />
			</Accordion>
		</Index>
	);
};

const HitsAccordionContents = ({ tab, ...props }) => {
	const { results, items } = useHits(props);
	return <TaskListContents tab={tab} items={items} total={results?.nbHits} />;
};

const TaskListContents = ({ tab, items = [], total }) => {
	const theme = useTheme();
	const count = total ?? items.length;
	const title = tab.slice(0, 1).toUpperCase() + tab.slice(1);

	return (
		<>
			<AccordionSummary
				expandIcon={<ExpandMoreIcon />}
				aria-controls={`${tab}bh-content`}
				id={`${tab}bh-header`}
				sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
			>
				<Typography
					variant='subtitle1'
					{...(count > 0 && { fontWeight: 500 })}
				>{`${title} ${count > 0 ? `(${count})` : ''}`}</Typography>
			</AccordionSummary>
			<AccordionDetails>
				{count > 0 ? (
					items.map((task, index) => (
						<TaskAccordion
							key={`task-accordion-task-tab-${tab}-${index}`}
							task={task}
						/>
					))
				) : (
					<Typography>{`No ${tab} tasks`}</Typography>
				)}
			</AccordionDetails>
		</>
	);
};

export const TasksTab = () => {
	const location = useLocation();

	const [expanded, setExpanded] = useState(['overdue', 'upcoming', 'completed']);

	useEffect(() => {
		if (location.state?.task) {
			if (location.state.task?.completed) {
				setExpanded(['completed']);
			} else {
				setExpanded([
					isAfter(today, safelyParseDate(location.state.task?.dueDate))
						? 'overdue'
						: 'upcoming',
				]);
			}
		}
	}, [location?.state?.task]);

	const handleChange = (panel) => (event, isExpanded) => {
		setExpanded((prev) => {
			return prev.includes(panel) ? prev.filter((e) => e !== panel) : [...prev, panel];
		});
	};

	const tabs = ['overdue', 'upcoming', 'completed'];
	return (
		<Box>
			{tabs.map((tab) => (
				<TaskTabContent
					key={tab}
					tab={tab}
					expanded={expanded.includes(tab)}
					setExpanded={handleChange}
				/>
			))}
		</Box>
	);
};
TasksTab.displayName = 'TasksTab';
